import React, {useCallback, useEffect} from 'react';
import {BiomatchResponse, CompareBiomatchResponse} from '@chekin/api';
import {IV_DOCUMENT_IMAGE_TYPES} from '@chekin/core';
import {ANALYTIC_EVENTS, Analytics} from '../../../analytics';
import {
  IVCheckProps,
  SendSelfieAndDocumentToCompareOptions,
  useBiomatch,
} from '../../useBiomatch';
import {BiomatchFlowActions, BiomatchFlowContext, BiomatchFlowHandlers} from './types';
import {buildBiomatchImageDetails, shouldRunOCRAfterBiomatch} from './helpers';

type UseBiomatchFlowProps = {
  context: BiomatchFlowContext;
  actions: BiomatchFlowActions;
  handlers: BiomatchFlowHandlers;
};

function useBiomatchFlow({context, actions, handlers}: UseBiomatchFlowProps) {
  const {
    docType,
    documentType,
    frontSideImage,
    reservationId,
    nationality,
    isBiomatchAndPassport,
  } = context;
  const {saveBiomatchImageDetails, saveComparisonSuccess, runOCR, finishFlow} = actions;
  const {onError, onBefore, onRetryLimitReached, onFailed} = handlers;

  const compareRef = React.useRef<
    null | ((args: SendSelfieAndDocumentToCompareOptions) => void)
  >(null);

  const handleBiomatchSuccess = useCallback(
    async (image: string, data: BiomatchResponse = {} as BiomatchResponse) => {
      if (docType === IV_DOCUMENT_IMAGE_TYPES.selfie) {
        const docTypeImageDetails = buildBiomatchImageDetails(image, docType, data);
        await saveBiomatchImageDetails(docType, docTypeImageDetails);
        Analytics.track(ANALYTIC_EVENTS.ivFinishingSelfie);
        compareRef.current?.({
          reservationId,
          documentPhotoId: frontSideImage.id || '',
          personPhotoId: data?.id || '',
        });
        return;
      }

      if (docType) {
        const docTypeImageDetails = buildBiomatchImageDetails(image, docType, data);
        await saveBiomatchImageDetails(docType, docTypeImageDetails);
        Analytics.track(ANALYTIC_EVENTS.ivFinishingDocumentFace, {
          side: docType,
        });
      }

      if (
        shouldRunOCRAfterBiomatch({
          isBiomatchAndPassport,
          documentTypeWithoutMrz: documentType?.without_mrz,
        })
      ) {
        void runOCR({
          documentType: documentType?.value,
          nationality,
          source: data.source || '',
          countryCode: 'ES',
          reservationId,
          image,
        });
        return;
      }

      finishFlow();
    },
    [
      docType,
      documentType?.without_mrz,
      documentType?.value,
      finishFlow,
      frontSideImage.id,
      isBiomatchAndPassport,
      nationality,
      reservationId,
      runOCR,
      saveBiomatchImageDetails,
    ],
  );

  const handleCompareSuccess = useCallback(
    async (data: CompareBiomatchResponse) => {
      await saveComparisonSuccess(data);
      Analytics.track(ANALYTIC_EVENTS.ivFinishingMatching);
      finishFlow();
    },
    [finishFlow, saveComparisonSuccess],
  );

  const {
    sendAndVerifySelfieForFaceDetection,
    sendSelfieAndDocumentToCompare,
    sendAndVerifyDocumentForFaceDetection,
  } = useBiomatch({
    onSuccessComparing: handleCompareSuccess,
    onRetriesFinish: onRetryLimitReached,
    onDetectionFailed: onFailed,
    onFinish: handleBiomatchSuccess,
    onError,
    onBefore,
  });

  const sendAndVerifyDocumentToBiomatchAndOCR = useCallback(
    async (props: IVCheckProps) => {
      void sendAndVerifyDocumentForFaceDetection(props);
    },
    [sendAndVerifyDocumentForFaceDetection],
  );

  const sendSelfieAndDocumentToCompareAndGetOCRData = useCallback(
    async (data: SendSelfieAndDocumentToCompareOptions) => {
      void sendSelfieAndDocumentToCompare(data);
    },
    [sendSelfieAndDocumentToCompare],
  );

  useEffect(() => {
    compareRef.current = sendSelfieAndDocumentToCompareAndGetOCRData;
  }, [sendSelfieAndDocumentToCompareAndGetOCRData]);

  return {
    sendAndVerifySelfieForFaceDetection,
    sendAndVerifyDocumentToBiomatchAndOCR,
    sendSelfieAndDocumentToCompare: sendSelfieAndDocumentToCompareAndGetOCRData,
  };
}

export {useBiomatchFlow};
